import { cloneDeep, flatMap, lowerFirst } from "lodash";
import { getEventModelName } from "@/entities/data-model";
import type { Relations } from "@/entities/data-model";
import type {
    EventFieldsGroup,
    FamilyRelation,
    FormMetadataResponse,
    NameFieldGroup,
    OtherFieldGroup,
    RelationshipFieldGroup,
} from "@/entities/form-metadata";
import { EventAssertion, GroupType, NameAssertion, SpecialAssertion } from "@/entities/form-metadata";
import type { Person } from "@/entities/tree-person";
import { LifeEventType, Relationship } from "@/entities/tree-person";

const FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING = {
    [Relationship.Father]: "father",
    [Relationship.Mother]: "mother",
    [Relationship.Spouse]: "spouse",
    [Relationship.Sibling]: "sibling",
    [Relationship.Child]: "child",
} as const;

const cleanFamilyMemberInstances = (currentFamilyMembers: Relations, familyMembers: Person["FamilyMembers"]) => {
    familyMembers.forEach((familyMember) => {
        if (FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]) {
            currentFamilyMembers[FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]].instances = [];
        }
    });
};

const populateFamilyMemberInstances = (
    currentFamilyMembers: Relations,
    familyMembers: Person["FamilyMembers"],
    allowedFamilyMembersObjects: Map<ReturnType<typeof lowerFirst<FamilyRelation>>, Set<NameAssertion>>,
) => {
    familyMembers.forEach((familyMember) => {
        if (
            FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship] &&
            allowedFamilyMembersObjects.has(FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship])
        ) {
            const allowedFamilyMemberFieldsObject = allowedFamilyMembersObjects.get(
                FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship],
            );
            const isGivenNameAllowed = allowedFamilyMemberFieldsObject?.has(NameAssertion.GivenName);
            const isSurnameAllowed = allowedFamilyMemberFieldsObject?.has(NameAssertion.Surname);
            // @ts-ignore FIXME
            currentFamilyMembers[FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]].instances.push({
                givenName: isGivenNameAllowed ? familyMember.GivenName : "",
                surname: isSurnameAllowed ? familyMember.Surname : "",
                secondSurname: "",
                surnameExactness: {
                    flags: {
                        phonetic: false,
                        similar: false,
                        soundex: false,
                    },
                    isExact: false,
                },
                givenNameExactness: {
                    flags: {
                        phonetic: false,
                        similar: false,
                        initials: false,
                    },
                    isExact: false,
                },
            });
        }
    });
};

const obtainFamilyMembersGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) =>
            "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.FamilyMember,
    );
const obtainAllowedFamilyMemberFields = (familyMembersGroups: RelationshipFieldGroup[]) => {
    const allowedFamilyMembersObjects = new Map<ReturnType<typeof lowerFirst<FamilyRelation>>, Set<NameAssertion>>();
    const familyMember = flatMap(familyMembersGroups, (familyMembersGroup) => familyMembersGroup.fieldsets);
    familyMember.forEach((relationship) => {
        const currentStateOfSet = allowedFamilyMembersObjects.get(lowerFirst(relationship.relation)) ?? new Set();
        const unwrappedFamilyMemberFields = new Set(flatMap(relationship.fields, (field) => field.assertion));
        allowedFamilyMembersObjects.set(
            lowerFirst(relationship.relation),
            new Set([...Array.from(currentStateOfSet), ...Array.from(unwrappedFamilyMemberFields)]),
        );
    });

    return allowedFamilyMembersObjects;
};

export const getFamilyMemberDetails = <T extends { family: Relations }>(
    familyMembers: Person["FamilyMembers"],
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["family"] => {
    const familyMembersGroups = obtainFamilyMembersGroups(formMetadata);
    const allowedFamilyMembersObjects = obtainAllowedFamilyMemberFields(familyMembersGroups);

    const currentFamilyMembers = cloneDeep(currentFormValue.family);
    cleanFamilyMemberInstances(currentFamilyMembers, familyMembers);
    populateFamilyMemberInstances(currentFamilyMembers, familyMembers, allowedFamilyMembersObjects);

    return currentFamilyMembers;
};

const EVENT_TO_DETAILS_MAPPING = {
    [LifeEventType.Birth]: "SelfBirth",
    [LifeEventType.Death]: "SelfDeath",
    [LifeEventType.Marriage]: "SelfMarriage",
    [LifeEventType.Residence]: "SelfResidence",
    [LifeEventType.Any]: "Self",
} as const;

const cleanEventInstances = <T extends Record<string, { instances: any[] }>>(
    currentEvents: T,
    lifeEvents: Person["LifeEvents"],
) => {
    lifeEvents.forEach((lifeEvent) => {
        if (EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] && currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]) {
            currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]!.instances = [];
        }
    });
};

const populateEventInstances = <T extends Record<string, { instances: any[] }>>(
    currentEvents: T,
    lifeEvents: Person["LifeEvents"],
    allowedEventObjects: Map<ReturnType<typeof getEventModelName>, Set<EventAssertion>>,
) => {
    lifeEvents.forEach((lifeEvent) => {
        if (
            EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] &&
            allowedEventObjects.has(EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] as ReturnType<typeof getEventModelName>) &&
            currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]
        ) {
            const allowedEventFieldsObject = allowedEventObjects.get(
                EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] as ReturnType<typeof getEventModelName>,
            );
            const isDayAllowed = allowedEventFieldsObject?.has(EventAssertion.Day);
            const isMonthAllowed = allowedEventFieldsObject?.has(EventAssertion.Month);
            const isYearAllowed = allowedEventFieldsObject?.has(EventAssertion.Year);
            const isPlaceAllowed = allowedEventFieldsObject?.has(EventAssertion.Place);
            currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]!.instances.push({
                date: {
                    year: isYearAllowed ? lifeEvent.Date.Year : null,
                    month: isMonthAllowed ? lifeEvent.Date.Month : null,
                    day: isDayAllowed ? lifeEvent.Date.Day : null,
                },
                dateProximity: {
                    year: null,
                    month: null,
                    day: null,
                },
                gpid: lifeEvent.LocationGpids?.Gpid,
                location: isPlaceAllowed ? lifeEvent.LocationText : "",
                locationExactness: null,
            });
        }
    });
};

const obtainEventGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) =>
            "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.Event,
    );
const obtainAllowedEventFields = (eventGroups: EventFieldsGroup[]) => {
    const allowedEventObjects = new Map<ReturnType<typeof getEventModelName>, Set<EventAssertion>>();
    const fieldsets = flatMap(eventGroups, (eventGroup) => eventGroup.fieldsets);

    fieldsets.forEach((fieldset) => {
        const currentStateOfSet =
            allowedEventObjects.get(
                getEventModelName({ eventType: fieldset.eventType, relation: fieldset.relation }),
            ) ?? new Set();
        const unwrappedEventFields = new Set(flatMap(fieldset.fields, (field) => field.assertion));

        allowedEventObjects.set(
            getEventModelName({ eventType: fieldset.eventType, relation: fieldset.relation }),
            new Set([...Array.from(currentStateOfSet), ...Array.from(unwrappedEventFields)]),
        );
    });

    return allowedEventObjects;
};

export const getEventsDetails = <T extends { events: Record<string, { instances: any[] }> }>(
    lifeEvents: Person["LifeEvents"],
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["events"] => {
    const eventGroups = obtainEventGroups(formMetadata);
    const allowedEventObjects = obtainAllowedEventFields(eventGroups);

    const currentEvents = cloneDeep(currentFormValue.events);
    cleanEventInstances(currentEvents, lifeEvents);
    populateEventInstances(currentEvents, lifeEvents, allowedEventObjects);

    return currentEvents;
};

const obtainNameGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) => "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === "name",
    );
const obtainAllowedNameFields = (nameGroups: NameFieldGroup[]) => {
    const foundAssertions = new Set<NameAssertion>();
    nameGroups.forEach((nameGroup) => {
        nameGroup.fields.forEach((field) => {
            foundAssertions.add(field.assertion);
        });
    });
    return foundAssertions;
};

export const getNameDetails = <T extends { name: any }>(
    person: Person,
    formMetadata: FormMetadataResponse,
): T["name"] => {
    const nameGroups = obtainNameGroups(formMetadata);
    const foundAssertions = obtainAllowedNameFields(nameGroups);

    return {
        givenName: foundAssertions.has(NameAssertion.GivenName) ? person.GivenName : "",
        surname: foundAssertions.has(NameAssertion.Surname) ? person.FatherSurname || person.Surname : "",
        secondSurname: foundAssertions.has(NameAssertion.SecondSurname) ? person.MotherSurname : "",
        givenNameExactness: {
            flags: {
                phonetic: false,
                similar: false,
                initials: false,
            },
            isExact: false,
        },
        surnameExactness: {
            flags: {
                phonetic: false,
                similar: false,
                soundex: false,
            },
            isExact: false,
        },
    };
};

const GENDER_FIELD_KEY = "SelfGender";

const hasGenderField = (formMetadata: FormMetadataResponse) => {
    for (const fieldGroupOrFeature of formMetadata.fieldGroupsAndFeatureContainers) {
        const isOtherGroup = "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.Other;

        if (isOtherGroup) {
            const otherGroup = fieldGroupOrFeature as OtherFieldGroup;
            const hasGender = otherGroup.others.some(
                (other) => other.assertion === SpecialAssertion.Gender && other.relation === "Self",
            );
            if (hasGender) return true;
        }
    }

    return false;
};

export const getGenderDetails = <T extends { fields: Record<string, any> }>(
    person: Person,
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["fields"] => {
    const isGenderFieldAllowed = hasGenderField(formMetadata);

    const currentFields = structuredClone(currentFormValue.fields);

    if (isGenderFieldAllowed) {
        currentFields[GENDER_FIELD_KEY] = {
            ...currentFields[GENDER_FIELD_KEY],
            value: person.Gender ?? "",
        };
    }

    return currentFields;
};
